import {
  signInWithEmailAndPassword,
  onAuthStateChanged,
  signOut,
  signInWithCustomToken,
  User,
  UserCredential,
  Auth,
  GoogleAuthProvider,
  signInWithPopup,
  getAdditionalUserInfo,
  createUserWithEmailAndPassword,
} from "firebase/auth";
import { useState, useEffect } from "react";
import { auth, db } from "../config/FirebaseConfig";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { Org } from "../types/basicTypes";
import { User as UserType } from "../types/basicTypes";

import { getDocDataById, getFlutterTime } from "../models/DataFunctions";
import sendNotifyDiscord from "../api/sendNotifyDiscord";

// Custom hook return type
interface UseAuthReturn {
  currentUser: User | null;
  loading: boolean;
}

// Function to log in the user with email and password
export const loginUser = async (
  email: string,
  password: string
): Promise<User> => {
  try {
    const userCredential: UserCredential = await signInWithEmailAndPassword(
      auth,
      email,
      password
    );
    return userCredential.user;
  } catch (error) {
    console.error("Error logging in:", (error as Error).message);
    throw error; // Throw the error so it can be handled in the component
  }
};

export const logoutUser = async (): Promise<void> => {
  try {
    console.warn("logoutUser");
    await signOut(auth); // Log out the user
  } catch (error) {
    console.error("Error logging out:", (error as Error).message);
    throw error; // Handle the error wherever this function is called
  }
};

const googleProvider = new GoogleAuthProvider();

// Optional: Add scopes if you need additional permissions
googleProvider.addScope("email");
googleProvider.addScope("profile");
export const loginWithEmailAndPassword = async (
  org_id: string | undefined,
  email: string,
  password: string,
  prefix: string
) => {
  try {
    // First, try to sign in with existing credentials
    const userCredential: UserCredential = await signInWithEmailAndPassword(
      auth,
      email,
      password
    );

    const user = userCredential.user;

    await sendNotifyDiscord(`User login: ${user.email} (${user.uid})`, prefix);

    console.log("Existing user logged in");
    // Optional: Update last login time
    await updateUserLastLogin(user);

    console.log("Login successful:", user);
    return user;
  } catch (error: any) {
    // If login fails due to user not found, create new account
    if (
      error.code === "auth/user-not-found" ||
      error.code === "auth/invalid-credential"
    ) {
      try {
        console.log("User not found, creating new account...");

        const newUserCredential: UserCredential =
          await createUserWithEmailAndPassword(auth, email, password);

        const newUser = newUserCredential.user;

        await sendNotifyDiscord(
          `New user created: ${newUser.email} (${newUser.uid})`,
          prefix
        );

        console.log("New user created, setting up organization...");
        await createUserAndOrganization(newUser, org_id);

        console.log("Account creation and login successful:", newUser);
        return newUser;
      } catch (createError) {
        console.error("Error creating new user:", createError);
        throw createError;
      }
    } else {
      // For other errors (wrong password, etc.), throw the original error
      console.error("Error logging in with email and password:", error);
      throw error;
    }
  }
};
// Google Sign In function
export const loginWithGoogle = async (
  org_id: string | undefined,
  prefix: string
) => {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    const user = result.user;

    // More reliable way to check if user is new
    const additionalUserInfo = getAdditionalUserInfo(result);
    const isNewUser = additionalUserInfo?.isNewUser;

    await sendNotifyDiscord(
      `New Google login: ${user.email} (${user.uid})`,
      prefix
    );

    if (isNewUser) {
      console.log("New user detected, creating organization...");
      await createUserAndOrganization(user, org_id);
    } else {
      console.log("Existing user logged in");
      // Optional: Update last login time
      await updateUserLastLogin(user);
    }

    console.log("Google login successful:", user);
    return user;
  } catch (error) {
    console.error("Google login error:", error);
    throw error;
  }
};

// Function to create user and organization data
const createUserAndOrganization = async (
  user: User,
  org_id: string | undefined
) => {
  try {
    let orgId = org_id;
    let orgData = {} as Org;

    // Create organization object
    if (!org_id) {
      orgId = generateId("org");
      orgData = {
        id: orgId,

        users_id: [user.uid],
        infos: {
          name:
            `Entreprise de ${user.displayName}` ||
            `Entreprise de ${user.email}`,
          email: user.email || "",
          phone: "",
          siret: "",
          city: "",
          postal: "",
          address: "",
        },
        creation_date: getFlutterTime(),
        counters: {
          attestations: 0, // Initialize counters
          xmls: 0,
          storage: 0, // Initialize storage used
        },
        facturation: {
          plan: "free", // Default plan for new organizations
          engagement: "test", // No engagement by default
          price: "0", // Default price
          next_billing_date: getFlutterTime(), // Set to current time, will be updated
          last_billing_date: getFlutterTime(), // Set to current time, will be updated
        },
      };
    } else {
      orgId = org_id;
      orgData = (await getDocDataById("org", orgId)) as Org;
      let newUsers = orgData.users_id || [];
      if (!newUsers.includes(user.uid)) {
        newUsers.push(user.uid);
      }
      orgData.users_id = newUsers;
    }

    // Create user object
    const userData: UserType = {
      id: user.uid,
      ref: doc(db, "user", user.uid),

      email: user.email || "",
      display_name: user.displayName || "Unknown User",
      org_id: orgId,
      creation_date: getFlutterTime(),
      last_updated: getFlutterTime(),
      last_login: getFlutterTime(),
      phone_number: user.phoneNumber || "",
      admin: false, // Default to true for new users
      maintenance_proof: false, // Default to false
      org_admin: true, // Default to true for new users
    };

    // Save to Firestore (batch write would be more efficient)
    await Promise.all([
      setDoc(doc(db, "org", orgId), orgData),
      setDoc(doc(db, "user", user.uid), userData),
    ]);

    console.log("User and organization created successfully");
  } catch (error) {
    console.error("Error creating user and organization:", error);
    throw error;
  }
};

// Function to update existing user's last login
const updateUserLastLogin = async (user: User) => {
  try {
    const userRef = doc(db, "users", user.uid);
    const userDoc = await getDoc(userRef);

    if (userDoc.exists()) {
      await setDoc(
        userRef,
        {
          lastLoginAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        },
        { merge: true }
      );
    }
  } catch (error) {
    console.error("Error updating user last login:", error);
  }
};

// Helper function to generate organization ID
const generateId = (collection: string) => {
  return (
    collection + `_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  );
};

// Function to get user with organization data
export const getUserWithOrganization = async (uid: string) => {
  try {
    const userDoc = await getDoc(doc(db, "users", uid));
    if (userDoc.exists()) {
      const userData = userDoc.data();
      const orgDoc = await getDoc(doc(db, "org", userData.org_id));

      return {
        user: userData,
        organization: orgDoc.exists() ? orgDoc.data() : null,
      };
    }
    return null;
  } catch (error) {
    console.error("Error getting user with organization:", error);
    throw error;
  }
};
// Custom hook to track auth state
export const useAuth = (app: any | null = null): UseAuthReturn => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true); // Loading indicator

  useEffect(() => {
    // Set up the listener
    const unsubscribe = onAuthStateChanged(auth, (user: User | null) => {
      if (user) {
        // User is logged in
        setCurrentUser(user);
      } else {
        // User is logged out
        setCurrentUser(null);
      }
      setLoading(false); // Loading finished
    });

    // Clean up the listener when the component unmounts
    return unsubscribe;
  }, []);

  return { currentUser, loading };
};

export async function signInAsGuest(
  token: string
): Promise<UserCredential | void> {
  try {
    const userCredential: UserCredential = await signInWithCustomToken(
      auth,
      token
    );
    return userCredential;
  } catch (error) {
    console.error("Guest sign-in failed:", error);
    // Consider whether you want to throw the error or handle it here
    throw error;
  }
}
